import { Input } from '@/components/ui/input';
import { forwardRef } from 'react';

interface PhoneInputProps extends Omit<React.ComponentProps<typeof Input>, 'onChange'> {
  value: string;
  onChange: (value: string) => void;
}

export const PhoneInput = forwardRef<HTMLInputElement, PhoneInputProps>(
  ({ value, onChange, ...props }, ref) => {
    // If value is E.164 (+1XXXXXXXXXX), convert to display format first
    const displayValue = value && value.startsWith('+1') 
      ? formatPhoneForDisplay(value)
      : value;

    const formatPhoneNumber = (input: string): string => {
      // Remove all non-digit characters
      const digits = input.replace(/\D/g, '');
      
      // Limit to 10 digits
      const limitedDigits = digits.slice(0, 10);
      
      // Format as (XXX) XXX-XXXX
      if (limitedDigits.length <= 3) {
        return limitedDigits;
      } else if (limitedDigits.length <= 6) {
        return `(${limitedDigits.slice(0, 3)}) ${limitedDigits.slice(3)}`;
      } else {
        return `(${limitedDigits.slice(0, 3)}) ${limitedDigits.slice(3, 6)}-${limitedDigits.slice(6)}`;
      }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const formatted = formatPhoneNumber(e.target.value);
      onChange(formatted);
    };

    return (
      <Input
        ref={ref}
        type="tel"
        value={displayValue}
        onChange={handleChange}
        placeholder="(555) 555-5555"
        {...props}
      />
    );
  }
);

PhoneInput.displayName = 'PhoneInput';

// Helper to convert formatted phone to E.164 format (+1XXXXXXXXXX)
export const formatPhoneForSupabase = (phone: string): string => {
  const digits = phone.replace(/\D/g, '');
  return digits.length === 10 ? `+1${digits}` : '';
};

// Helper to validate US phone number
export const isValidUSPhone = (phone: string): boolean => {
  const digits = phone.replace(/\D/g, '');
  return digits.length === 10;
};

// Helper to display E.164 phone in user-friendly format
export const formatPhoneForDisplay = (e164Phone: string): string => {
  if (!e164Phone || !e164Phone.startsWith('+1')) return e164Phone;
  
  const digits = e164Phone.slice(2); // Remove +1
  
  if (digits.length !== 10) return e164Phone;
  
  // Format as (XXX) XXX-XXXX
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};
